import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AskNaviChatStateService } from '../../services/ask-navi-chat-state.service';
import { ConversationListItem } from '../../services/ask-navi-copilot.service';

/**
 * Collapsible sidebar listing Ask Navi conversation history with a New chat action.
 */
@Component({
  selector: 'an-conversation-sidebar',
  standalone: true,
  imports: [CommonModule, ConfirmDialog, Button, Tooltip],
  providers: [ConfirmationService],
  templateUrl: './conversation-sidebar.component.html',
  styleUrl: './conversation-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationSidebarComponent {
  protected readonly state = inject(AskNaviChatStateService);
  private readonly _confirmation = inject(ConfirmationService);

  readonly collapsedChange = output<boolean>();

  onNewChat(): void {
    this.state.startNewChat();
  }

  onSelectConversation(id: string): void {
    void this.state.selectConversation(id);
  }

  onToggleCollapse(): void {
    this.state.toggleSidebar();
    this.collapsedChange.emit(this.state.sidebarCollapsed());
  }

  /**
   * Prompts for confirmation before soft-deleting a conversation.
   *
   * @param event - Click event from the delete button (stopped to avoid row selection).
   * @param conv - Conversation list row to delete.
   */
  confirmDeleteConversation(event: Event, conv: ConversationListItem): void {
    event.stopPropagation();
    if (this.state.isStreaming()) return;

    const title = conv.title?.trim() || 'New Conversation';
    this._confirmation.confirm({
      message: `Delete "${title}"? This cannot be undone.`,
      header: 'Delete conversation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => void this.state.deleteConversation(conv.id),
    });
  }

  formatActivity(iso?: string): string {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  }
}
